import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fileDownloadUrl, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, FileText, Save } from "lucide-react";
import { toast } from "sonner";

export default function UserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [u, setU] = useState(null);
  const [name, setName] = useState("");
  const [editKyc, setEditKyc] = useState(false);
  const [kf, setKf] = useState({});

  const load = () => api.get(`/users/${id}/detail`).then((r) => { setU(r.data); setName(r.data.name); }).catch(() => nav("/users"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!u) return <p className="text-muted-foreground">Loading…</p>;
  const kyc = u.kyc || {};
  const bank = kyc.bank || {};
  const docs = u.kyc_docs || {};

  const saveName = async () => { await api.put(`/users/${id}`, { name }); toast.success("Saved"); load(); };
  const decide = async (action) => { await api.post(`/users/${id}/${action}`); toast.success(action); load(); };
  const flagKyc = async () => {
    const field = window.prompt("Which KYC field needs correction? (e.g. PAN, Bank)");
    if (!field) return;
    const message = window.prompt("Message to the user:") || "";
    await api.post(`/users/${id}/kyc-flag`, { field, message });
    toast.success("KYC flagged for re-verification");
    load();
  };
  const changePassword = async () => {
    const p = window.prompt("Set a new password for this user:");
    if (!p) return;
    await api.post(`/users/${id}/password`, { password: p });
    toast.success("Password updated");
  };
  const startEditKyc = () => {
    const k = u.kyc || {}, b = k.bank || {};
    setKf({
      full_legal_name: k.full_legal_name || "", whatsapp: k.whatsapp || "", pan: k.pan || "", aadhar: k.aadhar || "",
      address: k.address || "", city: k.city || "", state: k.state || "", pincode: k.pincode || "", gst_number: k.gst_number || "",
      acc_holder: b.acc_holder || "", bank_name: b.bank_name || "", account_number: b.account_number || "", ifsc: b.ifsc || "", upi_id: b.upi_id || "",
    });
    setEditKyc(true);
  };
  const saveKyc = async () => {
    const kycObj = {
      ...(u.kyc || {}),
      full_legal_name: kf.full_legal_name, whatsapp: kf.whatsapp, pan: kf.pan, aadhar: kf.aadhar,
      address: kf.address, city: kf.city, state: kf.state, pincode: kf.pincode, gst_number: kf.gst_number,
      bank: { ...((u.kyc || {}).bank || {}), acc_holder: kf.acc_holder, bank_name: kf.bank_name, account_number: kf.account_number, ifsc: kf.ifsc, upi_id: kf.upi_id },
    };
    await api.put(`/users/${id}`, { kyc: kycObj, kyc_completed: true });
    toast.success("KYC saved & marked submitted. Click 'Approve KYC' to grant access.");
    setEditKyc(false); load();
  };

  const Row = ({ k, v }) => (
    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{k}</p><p className="font-medium">{v || "—"}</p></div>
  );

  return (
    <div className="space-y-6" data-testid="user-detail-page">
      <Button variant="ghost" onClick={() => nav("/users")} data-testid="back-users"><ArrowLeft className="mr-2 h-4 w-4" />Back to Users</Button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl font-bold tracking-tight">{u.name}</h1>
          <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">{u.role}</Badge>
          {u.status === "pending" && <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-400">Pending</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${API}/users/${id}/pdf?auth=${getToken()}`} download><Button variant="secondary" data-testid="download-user-pdf"><Download className="mr-2 h-4 w-4" />Download PDF</Button></a>
          <Button variant="secondary" onClick={flagKyc} data-testid="flag-kyc-btn">Flag KYC</Button>
          <Button variant="secondary" onClick={changePassword} data-testid="change-password-btn">Change Password</Button>
          {(u.status === "pending" || u.status === "rejected") && <>
            <Button onClick={() => decide("approve")} data-testid="detail-approve">Approve</Button>
            {u.status === "pending" && <Button variant="destructive" onClick={() => decide("reject")} data-testid="detail-reject">Reject</Button>}
          </>}
          {u.status === "approved" && <Button variant="secondary" onClick={() => decide("suspend")} data-testid="detail-suspend">Suspend</Button>}
          {(u.status === "suspended" || u.status === "terminated") && <Button onClick={() => decide("reactivate")} data-testid="detail-reactivate">Reactivate</Button>}
          {u.status !== "terminated" && <Button variant="destructive" onClick={() => decide("terminate")} data-testid="detail-terminate">Terminate</Button>}
        </div>
      </div>

      <Card className="space-y-3 p-6">
        <h3 className="font-heading text-lg font-semibold">Account</h3>
        <div className="flex items-end gap-2">
          <div className="flex-1"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" className="mt-1" /></div>
          <Button onClick={saveName} data-testid="save-user-name"><Save className="mr-2 h-4 w-4" />Save</Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Row k="Email" v={u.email} />
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">KYC Status</p>
            {!u.kyc_completed
              ? <Badge variant="outline" className="mt-1 border-rose-500/30 bg-rose-500/10 text-rose-400" data-testid="kyc-status-badge">Not Submitted</Badge>
              : !u.is_approved
                ? <Badge variant="outline" className="mt-1 border-amber-500/30 bg-amber-500/10 text-amber-400" data-testid="kyc-status-badge">Submitted — Pending Approval</Badge>
                : <Badge variant="outline" className="mt-1 border-emerald-500/30 bg-emerald-500/10 text-emerald-400" data-testid="kyc-status-badge">Approved</Badge>}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">KYC Details</h3>
          {!editKyc
            ? <div className="flex gap-2">
                {u.kyc_completed && !u.is_approved && u.status !== "suspended" && u.status !== "terminated" &&
                  <Button size="sm" onClick={() => decide("approve")} data-testid="approve-kyc-btn">Approve KYC</Button>}
                <Button size="sm" variant="secondary" onClick={startEditKyc} data-testid="edit-kyc-btn">Edit KYC</Button>
              </div>
            : <div className="flex gap-2"><Button size="sm" variant="ghost" onClick={() => setEditKyc(false)}>Cancel</Button><Button size="sm" onClick={saveKyc} data-testid="save-kyc-btn"><Save className="mr-2 h-4 w-4" />Save KYC</Button></div>}
        </div>
        {editKyc ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[["full_legal_name", "Full Legal Name"], ["whatsapp", "WhatsApp"], ["pan", "PAN"], ["aadhar", "Aadhar"], ["address", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"], ["gst_number", "GST Number"], ["acc_holder", "A/C Holder"], ["bank_name", "Bank"], ["account_number", "Account No."], ["ifsc", "IFSC"], ["upi_id", "UPI ID"]].map(([k, lab]) => (
              <div key={k}><Label className="text-xs">{lab}</Label><Input value={kf[k] || ""} onChange={(e) => setKf({ ...kf, [k]: e.target.value })} data-testid={`kyc-edit-${k}`} className="mt-1" /></div>
            ))}
          </div>
        ) : !u.kyc_completed ? <p className="text-muted-foreground">KYC not submitted yet.</p> : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Row k="Full Legal Name" v={kyc.full_legal_name} />
            <Row k="WhatsApp" v={kyc.whatsapp} />
            <Row k="PAN" v={kyc.pan} />
            <Row k="Aadhar" v={kyc.aadhar} />
            <Row k="Address" v={kyc.address} />
            <Row k="City" v={kyc.city} />
            <Row k="State" v={kyc.state} />
            <Row k="Pincode" v={kyc.pincode} />
            <Row k="GST Registered" v={kyc.gst_registered ? "Yes" : "No"} />
            <Row k="GST Number" v={kyc.gst_number} />
            <Row k="A/C Holder" v={bank.acc_holder} />
            <Row k="Bank" v={bank.bank_name} />
            <Row k="Account No." v={bank.account_number} />
            <Row k="IFSC" v={bank.ifsc} />
            <Row k="UPI ID" v={bank.upi_id} />
          </div>
        )}
      </Card>

      {u.kyc_completed && (
        <Card className="p-6">
          <h3 className="mb-4 font-heading text-lg font-semibold">Documents</h3>
          <div className="flex flex-wrap gap-3">
            {["aadhar", "pan", "gst", "photo", "signature"].map((k) => docs[k] ? (
              <a key={k} href={fileDownloadUrl(docs[k].file_id)} target="_blank" rel="noreferrer">
                <Button variant="secondary" size="sm" data-testid={`doc-${k}`}><FileText className="mr-2 h-4 w-4" />{k}</Button>
              </a>
            ) : null)}
          </div>
        </Card>
      )}
    </div>
  );
}
